import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, Search, Loader } from 'lucide-react';
import { whatCmsService, DetectionResult } from '../../../lib/whatCmsService';
import { supabase } from '../../../lib/supabase';

interface CmsResult {
  name: string;
  confidence: number;
  version?: string;
  categories: string[];
}

interface CmsDetectionCardProps {
  siteUrl: string;
  prospectId?: string;
}

const CmsDetectionCard: React.FC<CmsDetectionCardProps> = ({ siteUrl, prospectId }) => {
  const [cmsResults, setCmsResults] = useState<CmsResult[]>([]);
  const [isDetecting, setIsDetecting] = useState(false);
  const [hasDetected, setHasDetected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formattedUrl, setFormattedUrl] = useState<string>(siteUrl);
  
  // Récupérer l'URL formatée et les résultats de détection depuis la base de données
  useEffect(() => {
    const fetchProspectData = async () => {
      if (!prospectId) {
        // Si pas d'ID prospect, utiliser l'URL fournie
        ensureProtocol(siteUrl);
        return;
      }
      
      try {
        // 1. Récupérer l'URL du prospect
        const { data: prospectData, error: prospectError } = await supabase
          .from('prospects')
          .select('site_url')
          .eq('id', prospectId)
          .single();
          
        if (prospectError) {
          console.error('Erreur lors de la récupération de l\'URL du prospect:', prospectError);
          ensureProtocol(siteUrl); // Fallback à l'URL fournie
        } else if (prospectData && prospectData.site_url) {
          ensureProtocol(prospectData.site_url);
        } else {
          ensureProtocol(siteUrl); // Fallback si pas de données
        }
        
        // 2. Charger les résultats de détection CMS existants
        const { data: cmsData, error: cmsError } = await supabase
          .from('cms_detection_results')
          .select('*')
          .eq('prospect_id', prospectId)
          .single();
        
        if (cmsError) {
          if (cmsError.code !== 'PGRST116') { // PGRST116 = no rows returned
            console.error('Erreur lors du chargement des résultats CMS:', cmsError);
          }
        } else if (cmsData) {
          
          
          // Convertir les données au format attendu par le composant (CmsResult[])
          const convertedResults: CmsResult[] = [];
          
          // 1. Ajouter le CMS principal s'il existe
          if (cmsData.cms_name) {
            convertedResults.push({
              name: cmsData.cms_name,
              confidence: cmsData.confidence || 90,
              version: cmsData.version || undefined,
              categories: ['CMS']
            });
          }
          
          // 2. Ajouter les autres technologies
          if (cmsData.technologies) {
            // S'assurer que technologies est un tableau
            const techArray = Array.isArray(cmsData.technologies) 
              ? cmsData.technologies 
              : typeof cmsData.technologies === 'string'
                ? JSON.parse(cmsData.technologies)
                : [];
            
            // Convertir chaque technologie au format CmsResult
            techArray.forEach((tech: any) => {
              // Éviter les doublons avec le CMS principal
              if (!cmsData.cms_name || tech.name !== cmsData.cms_name) {
                convertedResults.push({
                  name: tech.name,
                  confidence: 75, // Confiance par défaut pour les technologies secondaires
                  version: tech.version,
                  categories: tech.categories || []
                });
              }
            });
          }
          
          setCmsResults(convertedResults);
          setHasDetected(true);
        }
      } catch (err) {
        console.error('Exception lors de la récupération des données du prospect:', err);
        ensureProtocol(siteUrl); // Fallback à l'URL fournie
      }
    };
    
    fetchProspectData();
  }, [prospectId, siteUrl]);
  
  // S'assurer que l'URL a un protocole
  const ensureProtocol = (url: string) => {
    if (!url) return;
    
    let formattedUrl = url.trim();
    if (!formattedUrl.startsWith('http://') && !formattedUrl.startsWith('https://')) {
      formattedUrl = 'https://' + formattedUrl;
    }
    
    setFormattedUrl(formattedUrl);
  };

  // Fonction pour sauvegarder les résultats CMS dans la base de données
  const saveCmsResults = async (result: DetectionResult) => {
    if (!prospectId) {
      console.warn('Impossible de sauvegarder les résultats CMS: ID prospect manquant');
      return;
    }
    
    try {
      // Créer un objet adapté à la structure de la table cms_detection_results
      const cmsData = {
        prospect_id: prospectId,
        url: formattedUrl,
        detected: result.detected,
        cms_name: result.cms,
        confidence: result.confidence,
        version: result.version || null,
        logo_url: result.logo || null,
        details: result.details || null,
        technologies: result.technologies || null,
        social_profiles: result.socialProfiles || null,
      };
      
      
      // Sauvegarder dans la table cms_detection_results existante
      const { error } = await supabase
        .from('cms_detection_results')
        .upsert(cmsData, { onConflict: 'prospect_id' });
      
      if (error) {
        console.error('Erreur lors de la sauvegarde des résultats CMS:', error);
      } else {
      }
    } catch (err) {
      console.error('Exception lors de la sauvegarde des résultats CMS:', err);
    }
  };

  const detectCms = async () => {
    if (!formattedUrl) return;
    
    setIsDetecting(true);
    setError(null);
    
    try {
      
      const result: DetectionResult = await whatCmsService.detectCms(formattedUrl);
      
      // Convert DetectionResult to CmsResult[]
      const convertedResults: CmsResult[] = [];
      
      if (result.detected && result.cms) {
        convertedResults.push({
          name: result.cms,
          confidence: result.confidence,
          version: result.version,
          categories: ['CMS']
        });
      }
      
      // Add other technologies if available
      if (result.technologies) {
        result.technologies.forEach(tech => {
          convertedResults.push({
            name: tech.name,
            confidence: 75, // Default confidence for additional technologies
            version: tech.version,
            categories: tech.categories
          });
        });
      }
      
      setCmsResults(convertedResults);
      setHasDetected(true);
      
      // Sauvegarder les résultats dans la base de données
      await saveCmsResults(result);

    } catch (err) {
      console.error('Erreur lors de la détection CMS:', err);
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
      setCmsResults([]);
    } finally {
      setIsDetecting(false);
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 80) return 'text-green-600 bg-green-100';
    if (confidence >= 50) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getConfidenceIcon = (confidence: number) => {
    if (confidence >= 80) return <CheckCircle className="w-4 h-4" />;
    if (confidence >= 50) return <AlertCircle className="w-4 h-4" />;
    return <XCircle className="w-4 h-4" />;
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">Détection CMS</span>
        {!isDetecting && (
          <button
            onClick={() => detectCms()}
            className="inline-flex items-center px-2 py-1 text-xs font-medium text-orange-600 hover:text-orange-800"
            title="Détecter ou rafraîchir la détection CMS"
          >
            <Search className="w-3 h-3 mr-1" />
            {hasDetected ? 'Rafraîchir' : 'Détecter'}
          </button>
        )}
      </div>

      {isDetecting && (
        <div className="flex items-center justify-center py-4">
          <Loader className="w-4 h-4 animate-spin mr-2" />
          <span className="text-sm text-gray-600">Détection en cours...</span>
        </div>
      )}

      {error && (
        <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
          {error}
        </div>
      )}

      {hasDetected && cmsResults.length > 0 && (
        <div className="space-y-2">
          {cmsResults.map((result, index) => (
            <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
              <div className="flex items-center">
                {getConfidenceIcon(result.confidence)}
                <span className="ml-2 text-sm font-medium">{result.name}</span>
                {result.version && (
                  <span className="ml-1 text-xs text-gray-500">v{result.version}</span>
                )}
              </div>
              <span className={`px-2 py-1 rounded-full text-xs ${getConfidenceColor(result.confidence)}`}>
                {result.confidence}%
              </span>
            </div>
          ))}
        </div>
      )}

      {hasDetected && cmsResults.length === 0 && !error && (
        <div className="text-sm text-gray-500 italic">Aucun CMS détecté</div>
      )}
    </div>
  );
};

export default CmsDetectionCard;